import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { registerUser, clearError } from 'store/authSlice';
import {
  validateEmailField,
  validateUsernameField,
  validatePasswordField,
  validateConfirmPassword,
} from 'utils/validation';
import { Alert } from 'components/alert';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import styles from './register.module.scss';
import { ActionButton } from 'components/actionButton/actionButton';
import { showToast } from 'store/toastSlice';
import { EyeIcon, EyeOffIcon, CheckIcon, ChevronDownIcon } from 'components/icons';
import { getAndClearPendingGameRedirect } from 'utils/pendingRedirect';

const LazyGoogleButton = lazy(() => import('components/GoogleButton/GoogleButton'));

interface RegisterProps {
  onClose: () => void;
  onLoginClick?: () => void;
  referralFromUrl?: string;
  onLoadingChange?: (loading: boolean) => void;
}

const Register: React.FC<RegisterProps> = ({
  onClose,
  onLoginClick,
  referralFromUrl,
  onLoadingChange,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useLocalizedNavigate();
  const {
    isLoading,
    error: serverError,
    isAuthenticated,
    user,
  } = useAppSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isReferralOpen, setIsReferralOpen] = useState(!!referralFromUrl);

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    referralCode: referralFromUrl || '',
    agreed: false,
  });

  const [errors, setErrors] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    agreed: '',
  });

  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const config = useAppSelector((state) => state.site.config);
  const isBlocked = config?.geo?.isBlocked;

  const isAnyLoading = isLoading || isGoogleLoading;
  const isFormDisabled = isAnyLoading || isBlocked;

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isGoogleLoading);
    }
  }, [isGoogleLoading, onLoadingChange]);

  const hasHandledRegisterSuccessRef = useRef(false);

  // Close modal automatically if registration succeeds
  useEffect(() => {
    if (isAuthenticated && !hasHandledRegisterSuccessRef.current) {
      hasHandledRegisterSuccessRef.current = true;

      const userLang = user?.language || user?.profile?.language;
      const storedLang = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
      const targetLang = userLang || storedLang || 'en';

      const showRegisterToast = async () => {
        let toastText = t('auth.registerSuccess', 'You have successfully registered.');

        if (targetLang !== i18n.language) {
          try {
            await i18n.changeLanguage(targetLang);
            toastText = i18n.t('auth.registerSuccess', {
              lng: targetLang,
              defaultValue: 'You have successfully registered.',
            });
          } catch (error) {
            console.error('Failed to change language for register toast:', error);
          }
        }

        dispatch(showToast({ text: toastText, type: 'success' }));
      };

      showRegisterToast();

      onClose();
      // Clear pending redirect so it doesn't interfere later, but always go to home
      getAndClearPendingGameRedirect();
      navigate('/');
    }
  }, [isAuthenticated, user, onClose, navigate, dispatch, t, i18n]);

  // Clear server error when user starts typing again
  useEffect(() => {
    if (serverError) {
      dispatch(clearError());
    }
  }, [formData, dispatch, serverError]); // Only need to clear it when formData itself changes

  const handleRegister = () => {
    let isValid = true;
    const newErrors = {
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      agreed: '',
    };

    const emailError = validateEmailField(formData.email);
    if (emailError) {
      newErrors.email = emailError;
      isValid = false;
    }

    const usernameError = validateUsernameField(formData.username);
    if (usernameError) {
      newErrors.username = usernameError;
      isValid = false;
    }

    const passwordError = validatePasswordField(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
      isValid = false;
    }

    const confirmPasswordError = validateConfirmPassword(
      formData.password,
      formData.confirmPassword,
    );
    if (confirmPasswordError) {
      newErrors.confirmPassword = confirmPasswordError;
      isValid = false;
    }

    if (!formData.agreed) {
      newErrors.agreed = t('auth.agreeToTermsRequired', 'You must agree to the terms');
      isValid = false;
    }

    setErrors(newErrors);

    if (isValid) {
      dispatch(
        registerUser({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          referralCode: formData.referralCode || undefined,
        }),
      );
    }
  };

  return (
    <>
      <div className={styles.inputGroup}>
        <label htmlFor="reg-email">
          {t('common.email', 'Email')} <span className={styles.required}>*</span>
        </label>
        <input
          type="email"
          id="reg-email"
          className={`${styles.inputField} ${errors.email ? styles.inputError : ''}`}
          value={formData.email}
          onChange={(e) => {
            setFormData({ ...formData, email: e.target.value });
            if (errors.email) setErrors({ ...errors, email: '' });
          }}
          disabled={isFormDisabled}
        />
        {errors.email && <span className={styles.errorMsg}>{errors.email}</span>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="reg-username">
          {t('common.username', 'Username')} <span className={styles.required}>*</span>
        </label>
        <input
          type="text"
          id="reg-username"
          className={`${styles.inputField} ${errors.username ? styles.inputError : ''}`}
          value={formData.username}
          onChange={(e) => {
            setFormData({ ...formData, username: e.target.value });
            if (errors.username) setErrors({ ...errors, username: '' });
          }}
          disabled={isFormDisabled}
        />
        {errors.username && <span className={styles.errorMsg}>{errors.username}</span>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="reg-password">
          {t('common.password', 'Password')} <span className={styles.required}>*</span>
        </label>
        <div className={styles.passwordWrapper}>
          <input
            type={showPassword ? 'text' : 'password'}
            id="reg-password"
            className={`${styles.inputField} ${errors.password ? styles.inputError : ''}`}
            value={formData.password}
            onChange={(e) => {
              setFormData({ ...formData, password: e.target.value });
              if (errors.password) setErrors({ ...errors, password: '' });
            }}
            disabled={isFormDisabled}
          />
          <button
            type="button"
            className={styles.eyeBtn}
            onClick={() => !isFormDisabled && setShowPassword(!showPassword)}
            aria-label="Toggle password visibility"
            disabled={isFormDisabled}
          >
            {showPassword ? (
              <EyeIcon width="20" height="20" />
            ) : (
              <EyeOffIcon width="20" height="20" />
            )}
          </button>
        </div>
        {errors.password && <span className={styles.errorMsg}>{errors.password}</span>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="reg-confirm-password">
          {t('common.confirmPassword', 'Confirm Password')}{' '}
          <span className={styles.required}>*</span>
        </label>
        <div className={styles.passwordWrapper}>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            id="reg-confirm-password"
            className={`${styles.inputField} ${errors.confirmPassword ? styles.inputError : ''}`}
            value={formData.confirmPassword}
            onChange={(e) => {
              setFormData({ ...formData, confirmPassword: e.target.value });
              if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
            }}
            disabled={isFormDisabled}
          />
          <button
            type="button"
            className={styles.eyeBtn}
            onClick={() => !isFormDisabled && setShowConfirmPassword(!showConfirmPassword)}
            aria-label="Toggle confirm password visibility"
            disabled={isFormDisabled}
          >
            {showConfirmPassword ? (
              <EyeIcon width="20" height="20" />
            ) : (
              <EyeOffIcon width="20" height="20" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <span className={styles.errorMsg}>{errors.confirmPassword}</span>
        )}
      </div>

      <div className={styles.referralCollapse}>
        <button
          type="button"
          className={styles.referralToggle}
          onClick={() => !isFormDisabled && setIsReferralOpen(!isReferralOpen)}
          disabled={isFormDisabled}
        >
          {t('auth.referralCodeOptional', 'Referral Code (Optional)')}
          <ChevronDownIcon
            width="20"
            height="20"
            className={isReferralOpen ? styles.rotateIcon : ''}
          />
        </button>

        {isReferralOpen && (
          <div className={styles.referralBody}>
            <div className={styles.inputGroup}>
              <label htmlFor="reg-ref">{t('common.code', 'Code')}</label>
              <input
                type="text"
                id="reg-ref"
                className={styles.inputField}
                placeholder={t('auth.enterReferralCode', 'Enter your referral code')}
                value={formData.referralCode}
                onChange={(e) => setFormData({ ...formData, referralCode: e.target.value })}
                disabled={isFormDisabled}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.checkboxGroup}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={formData.agreed}
            onChange={(e) => {
              setFormData({ ...formData, agreed: e.target.checked });
              if (errors.agreed) setErrors({ ...errors, agreed: '' });
            }}
            className={styles.checkbox}
            disabled={isFormDisabled}
          />
          <span className={`${styles.customCheckbox} ${errors.agreed ? styles.inputError : ''}`}>
            {formData.agreed && <CheckIcon width="14" height="14" strokeWidth="3" />}
          </span>
          {t('auth.termsAgreement', 'I am 18 or older and agree to the terms')}
          <span className={styles.required}>*</span>
        </label>
        {errors.agreed && <span className={styles.errorMsg}>{errors.agreed}</span>}
      </div>

      <Alert message={serverError || undefined} type="error" />

      <ActionButton
        className={styles.submitBtn}
        onClick={handleRegister}
        disabled={isFormDisabled}
        isLoading={isAnyLoading}
      >
        {t('common.signUp', 'Sign Up')}
      </ActionButton>

      {!referralFromUrl && !formData.referralCode && (
        <>
          <div className={styles.divider}>
            <span>{t('auth.orContinueWith', 'Or continue with')}</span>
          </div>

          <Suspense
            fallback={
              <button className={styles.googleBtn} disabled>
                {isFormDisabled
                  ? t('auth.registerWithGoogle', 'Register with Google')
                  : t('auth.loadingGoogle', 'Loading Google Auth...')}
              </button>
            }
          >
            <LazyGoogleButton
              disabled={isFormDisabled}
              setIsGoogleLoading={setIsGoogleLoading}
              text={t('auth.registerWithGoogle', 'Register with Google')}
            />
          </Suspense>
        </>
      )}

      <div className={styles.loginPrompt}>
        {t('auth.alreadyHaveAccount', 'Already have an account?')}{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (isAnyLoading) return;
            if (onLoginClick) {
              onClose();
              onLoginClick();
            }
          }}
          className={isAnyLoading ? styles.disabledLink : ''}
        >
          {t('auth.signIn', 'Sign in')}
        </a>
      </div>
    </>
  );
};

export default Register;
